import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { userMaySeeActivitiesImportNav } from "../lib/adminImport";
import { fetchSchoolEmails, submitSchoolEmailReview, uploadSchoolEmailPdf, patchSchoolEmailEventOverride, fetchSchoolExtractionStats } from "../api/schoolEmails";
import { forgetExtraction, purgeExtractionSnapshot, restoreExtraction } from "../api/extractions";
import {
  buildGoogleCalendarUrl,
  buildIcs,
  buildOutlookCalendarUrl,
} from "../lib/googleCalendarLink";
import { groupSchoolEmailEvents } from "../lib/groupSchoolEmailEvents";
import { partitionMailEvents, todayYmdZurich, isEventActionType } from "../lib/schoolAttention";
import { shareTerminViaWhatsApp } from "../lib/shareTermin";
import {
  hasSchoolEventOverride,
  isSchoolEventDismissed,
  resolveSchoolEvent,
} from "../lib/resolveSchoolEvent";
import { fetchFamilyProfile } from "../api/familyProfile";
import { fetchHousehold } from "../api/household";
import { isSchoolEventRemindEnabled } from "../lib/schoolReminderPrefs";
import { trackEvent } from "../analytics";
import { ANALYTICS } from "../lib/analyticsEvents";
import {
  isFirstExtractionCelebrated,
  markFirstExtractionCelebrated,
  isSharePromptShown,
  markSharePromptShown,
  markShareBannerDismissed,
  shouldShowShareBanner,
  getSharePromptContext,
  trackSchoolCalendarTap,
} from "../lib/wedgeFunnel";
import {
  trackSharePromptDismissed,
  trackSharePromptShown,
} from "../lib/sharePromptAnalytics";
import {
  canShareKiTaExtract,
  getShareableKiTaEvents,
  shareKiTaExtractViaWhatsApp,
  shareKiTaPartnerViaWhatsApp,
  shareKiTaPeerViaWhatsApp,
} from "../lib/shareKiTaWin";
import { buildKitaShareChatMessage } from "../lib/schoolSetupChat";
import "./SchoolInboxPage.css";

export const KITA_UPCOMING_DAYS = 14;
const EMPTY_CORRECTIONS = Object.freeze({});
const EMPTY_KITA_DAYS = Object.freeze([]);

const ACTION_LABELS = {
  task: "Aufgabe",
  event: "Termin",
  prep: "Aufgabe",
  bring: "Aufgabe",
  info: "Info",
};

const CONFIDENCE_LABELS = {
  high: "hoch",
  medium: "mittel",
  low: "niedrig",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function formatEventDate(event) {
  if (!event?.date) return "Kein Datum erkannt";
  const time = event.all_day === false && event.time ? ` ${event.time}` : "";
  const end = event.end_date && event.end_date !== event.date ? `–${event.end_date}` : "";
  return `${event.date}${end}${time}`;
}

function formatOverrideDiff(rawEvent, corrections, index) {
  const override = corrections?.userOverrides?.[index];
  if (!override) return null;
  const parts = [];
  if (override.date && override.date !== rawEvent?.date) {
    parts.push(`Datum: ${rawEvent?.date || "—"} → ${override.date}`);
  }
  if (override.time !== undefined && override.time !== rawEvent?.time) {
    parts.push(`Uhrzeit: ${rawEvent?.time || "—"} → ${override.time || "ganztägig"}`);
  }
  if (override.all_day === true && rawEvent?.all_day === false) {
    parts.push("→ ganztägig");
  }
  return parts.length ? parts.join(" · ") : "Korrigiert";
}

function EventEditSheet({ event, open, onClose, onSave, saving, error }) {
  const [date, setDate] = useState(event?.date || "");
  const [time, setTime] = useState(event?.time || "");
  const [allDay, setAllDay] = useState(event?.all_day !== false && !event?.time);

  useEffect(() => {
    if (!open) return;
    setDate(event?.date || "");
    setTime(event?.time || "");
    setAllDay(event?.all_day !== false && !event?.time);
  }, [open, event]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (allDay) {
      onSave({ date: date || undefined, all_day: true, time: null });
      return;
    }
    onSave({ date: date || undefined, time: time || undefined, all_day: false });
  };

  return (
    <div className="school-edit-backdrop" role="presentation" onClick={onClose}>
      <form
        className="school-edit-sheet"
        role="dialog"
        aria-labelledby="school-edit-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h4 id="school-edit-title" className="school-edit-title">
          Termin anpassen
        </h4>
        {event?.title ? <p className="school-edit-event-title">{event.title}</p> : null}
        {event?.source_quote ? (
          <p className="school-event-quote">„{event.source_quote}"</p>
        ) : null}
        <label className="school-edit-field">
          <span>Datum</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="school-edit-field school-edit-field--row">
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => {
              setAllDay(e.target.checked);
              if (e.target.checked) setTime("");
            }}
          />
          <span>Ganztägig</span>
        </label>
        {!allDay ? (
          <label className="school-edit-field">
            <span>Uhrzeit</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </label>
        ) : null}
        <div className="school-edit-actions">
          <button type="submit" className="school-btn school-btn--primary" disabled={saving}>
            {saving ? "Speichert…" : "Speichern"}
          </button>
          <button type="button" className="school-btn" disabled={saving} onClick={onClose}>
            Abbrechen
          </button>
        </div>
        {error ? <p className="school-error">{error}</p> : null}
      </form>
    </div>
  );
}

function downloadIcs(event) {
  const ics = buildIcs(event);
  if (!ics) return;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(event.title || "termin").replace(/[^\w-]+/g, "_").slice(0, 40)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function EventRow({
  event,
  rawEvent,
  corrections,
  adminMode,
  wrong,
  onToggleWrong,
  extractionId,
  eventIndex,
  analyticsSource = "inbox",
  onCalendarTap,
  onCorrectionsChange,
  onIrrelevant,
  kitaWeekdays = EMPTY_KITA_DAYS,
}) {
  const resolved = resolveSchoolEvent(rawEvent || event, corrections, eventIndex) || rawEvent || event;
  const calUrl = buildGoogleCalendarUrl(resolved);
  const outlookUrl = buildOutlookCalendarUrl(resolved);
  const canAddToCalendar = Boolean(calUrl || outlookUrl);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [remindSaving, setRemindSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [remindEnabled, setRemindEnabled] = useState(() =>
    isSchoolEventRemindEnabled(rawEvent || event, corrections, eventIndex, kitaWeekdays),
  );

  useEffect(() => {
    setRemindEnabled(isSchoolEventRemindEnabled(rawEvent || event, corrections, eventIndex, kitaWeekdays));
  }, [rawEvent, event, corrections, eventIndex, kitaWeekdays]);

  const trackCalendar = (provider) => {
    if (adminMode) return;
    onCalendarTap?.(provider);
  };

  const handleRemindToggle = async () => {
    if (adminMode || extractionId == null) return;
    const next = !remindEnabled;
    setRemindEnabled(next);
    setRemindSaving(true);
    try {
      await patchSchoolEmailEventOverride(extractionId, eventIndex, { remind: next });
      if (onCorrectionsChange) onCorrectionsChange();
    } catch (err) {
      setRemindEnabled(!next);
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemindSaving(false);
    }
  };

  const handleSaveOverride = async (body) => {
    setEditError(null);
    setSaving(true);
    try {
      await patchSchoolEmailEventOverride(extractionId, eventIndex, body);
      setEditOpen(false);
      if (onCorrectionsChange) onCorrectionsChange();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleShareTermin = async () => {
    if (adminMode) return;
    setSharing(true);
    try {
      await shareTerminViaWhatsApp({
        ...resolved,
        source: analyticsSource,
      });
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setSharing(false);
    }
  };

  const overrideDiff =
    adminMode && corrections ? formatOverrideDiff(rawEvent || event, corrections, eventIndex) : null;
  const edited = hasSchoolEventOverride(corrections, eventIndex);

  return (
    <>
      <li className={`school-event${wrong ? " school-event--wrong" : ""}${edited ? " school-event--edited" : ""}`}>
        <div className="school-event-main">
          <p className="school-event-title">{resolved.title || "(ohne Titel)"}</p>
          <p className="school-event-meta">
            <span className="school-event-date">{formatEventDate(resolved)}</span>
            {edited && !adminMode ? (
              <span className="school-event-tag school-event-tag--edited">angepasst</span>
            ) : null}
            {resolved.child ? <span className="school-event-tag">{resolved.child}</span> : null}
            <span className="school-event-tag">
              {ACTION_LABELS[resolved.action_type] || resolved.action_type}
            </span>
            {adminMode ? (
              <span className="school-event-tag school-event-tag--conf">
                Sicherheit: {CONFIDENCE_LABELS[resolved.confidence] || resolved.confidence}
              </span>
            ) : null}
          </p>
          {overrideDiff ? (
            <p className="school-event-override-diff">Nutzer: {overrideDiff}</p>
          ) : null}
          {resolved.source_quote ? (
            <p className="school-event-quote">„{resolved.source_quote}"</p>
          ) : null}
        </div>
        <div className="school-event-actions">
          {!adminMode && extractionId != null ? (
            <button
              type="button"
              className={`school-btn school-btn--remind${remindEnabled ? " school-btn--remind-on" : ""}`}
              aria-pressed={remindEnabled}
              aria-label={remindEnabled ? "Erinnerung ausschalten" : "Erinnern"}
              title={remindEnabled ? "Erinnerung an" : "Erinnerung aus"}
              disabled={remindSaving}
              onClick={handleRemindToggle}
            >
              {remindEnabled ? "🔔" : "🔕"}
            </button>
          ) : null}
          {!adminMode ? (
            <button
              type="button"
              className="school-btn"
              onClick={() => {
                setEditError(null);
                setEditOpen(true);
              }}
            >
              Anpassen
            </button>
          ) : null}
          {!adminMode && extractionId != null ? (
            <button
              type="button"
              className="school-btn school-btn--quiet"
              disabled={saving}
              onClick={async () => {
                setEditError(null);
                setSaving(true);
                try {
                  await patchSchoolEmailEventOverride(extractionId, eventIndex, { dismiss: true });
                  onIrrelevant?.({
                    extractionId,
                    eventIndex,
                    title: resolved.title,
                  });
                  if (onCorrectionsChange) onCorrectionsChange();
                } catch (err) {
                  setEditError(err instanceof Error ? err.message : String(err));
                } finally {
                  setSaving(false);
                }
              }}
            >
              Nicht relevant
            </button>
          ) : null}
          {calUrl ? (
            <a
              className="school-btn school-btn--primary"
              href={calUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackCalendar("google");
              }}
            >
              Zu Google Kalender
            </a>
          ) : null}
          {outlookUrl ? (
            <a
              className="school-btn school-btn--primary"
              href={outlookUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackCalendar("outlook");
              }}
            >
              Zu Outlook
            </a>
          ) : null}
          {!canAddToCalendar ? (
            <span className="school-event-nodate">Ohne Datum – kein Kalendereintrag</span>
          ) : null}
          {!adminMode && isEventActionType(resolved.action_type) ? (
            <button
              type="button"
              className="school-btn"
              disabled={sharing}
              onClick={handleShareTermin}
            >
              {sharing ? "…" : "Teilen"}
            </button>
          ) : null}
          {canAddToCalendar ? (
            <button
              type="button"
              className="school-btn"
              onClick={() => {
                if (!adminMode && extractionId != null) {
                  trackEvent(ANALYTICS.school_ics_download, {
                    extraction_id: extractionId,
                    event_index: eventIndex,
                    source: analyticsSource,
                  });
                }
                downloadIcs(resolved);
              }}
            >
              .ics
            </button>
          ) : null}
          {adminMode ? (
            <label className="school-event-wrong">
              <input
                type="checkbox"
                checked={wrong}
                onChange={(e) => onToggleWrong(e.target.checked)}
              />
              Falsch
            </label>
          ) : null}
        </div>
      </li>
      {!adminMode ? (
        <EventEditSheet
          event={resolved}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSave={handleSaveOverride}
          saving={saving}
          error={editError}
        />
      ) : null}
    </>
  );
}

function isPdfStillProcessing(item) {
  const status = item?.processingStatus || "ready";
  const pdfs = item?.attachmentsMeta || [];
  const pendingRemote = pdfs.some((a) => a.kind === "pdf" && a.pending === true);
  const slowDownload = pdfs.some(
    (a) => a.kind === "pdf" && a.pending === true && Number(a.downloadAttempts ?? 0) >= 2
  );
  if (slowDownload) return false;
  if (
    status === "pending_attachments" ||
    status === "processing_attachments"
  ) {
    return true;
  }
  return pendingRemote;
}

function needsPdfUploadWhilePending(item) {
  return (item?.attachmentsMeta || []).some(
    (a) =>
      a.kind === "pdf" &&
      a.pending === true &&
      Number(a.downloadAttempts ?? 0) >= 2 &&
      !a.parsed
  );
}

function needsPdfUpload(item) {
  if (isPdfStillProcessing(item)) return false;
  const pdfs = (item?.attachmentsMeta || []).filter((a) => a.kind === "pdf");
  if (pdfs.length === 0) return false;
  return pdfs.some((a) => a.excluded || !a.parsed);
}

function linkedDocumentOnly(item) {
  const pdfs = (item?.attachmentsMeta || []).filter((a) => a.kind === "pdf");
  return pdfs.length > 0 && pdfs.every((a) => a.source === "linked_document");
}

function PdfProcessingBlock({ showUploadHint, linked }) {
  return (
    <div className="school-pdf-processing">
      <p className="school-pdf-processing-text">
        {linked
          ? "Verlinkte Hauszeitung wird gelesen — das kann einen Moment dauern."
          : "PDF-Anhang wird ausgewertet (Text + OCR) — das kann etwa 30 Sekunden dauern."}
      </p>
      {showUploadHint ? (
        <p className="school-pdf-processing-text">
          Der Download dauert länger als erwartet — du kannst das PDF auch direkt hochladen.
        </p>
      ) : null}
    </div>
  );
}

function PdfUploadBlock({ item, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const excludedPdfs = (item.attachmentsMeta || []).filter(
    (a) => a.kind === "pdf" && a.excluded
  );

  const handleUpload = async () => {
    if (!file) return;
    setError(null);
    setSuccessMsg(null);
    setUploading(true);
    try {
      const result = await uploadSchoolEmailPdf(item.id, file);
      setFile(null);
      if (result.pdfParsed) {
        setSuccessMsg("PDF ausgewertet — Termine aktualisiert.");
      } else {
        setSuccessMsg(
          "PDF hochgeladen, aber kein lesbarer Text gefunden (evtl. Scan)."
        );
      }
      if (onUploaded) onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="school-pdf-upload">
      <p className="school-pdf-upload-lead">
        {linkedDocumentOnly(item)
          ? "Die verlinkte Hauszeitung konnte nicht gelesen werden — Termine fehlen evtl."
          : excludedPdfs.length > 0
            ? `Anhang «${excludedPdfs[0].filename}» konnte nicht automatisch gelesen werden — Termine fehlen evtl.`
            : "PDF-Anhang konnte nicht gelesen werden — Termine fehlen evtl."}
      </p>
      <p className="school-pdf-upload-hint">
        {linkedDocumentOnly(item)
          ? "Wenn du das PDF hast, lade es hier hoch."
          : "PDF aus Gmail herunterladen und hier hochladen."}
      </p>
      <div className="school-pdf-upload-row">
        <input
          type="file"
          accept="application/pdf,.pdf"
          className="school-pdf-upload-input"
          onChange={(e) => {
            setFile(e.target.files?.[0] || null);
            setError(null);
            setSuccessMsg(null);
          }}
        />
        <button
          type="button"
          className="school-btn school-btn--primary"
          disabled={!file || uploading}
          onClick={handleUpload}
        >
          {uploading ? "Wird ausgewertet…" : "PDF auswerten"}
        </button>
      </div>
      {successMsg ? <p className="school-pdf-upload-ok">{successMsg}</p> : null}
      {error ? <p className="school-error">{error}</p> : null}
    </div>
  );
}

function InfoRow({ event, adminMode, wrong, onToggleWrong }) {
  return (
    <li className={`school-info-item${wrong ? " school-info-item--wrong" : ""}`}>
      <div className="school-info-item-main">
        <p className="school-info-item-title">{event.title || "(Info)"}</p>
        {event.source_quote ? (
          <p className="school-event-quote">„{event.source_quote}"</p>
        ) : null}
      </div>
      {adminMode ? (
        <label className="school-event-wrong">
          <input
            type="checkbox"
            checked={wrong}
            onChange={(e) => onToggleWrong(e.target.checked)}
          />
          Falsch
        </label>
      ) : null}
    </li>
  );
}

function ExtractionSection({ title, items, emptyLabel, renderRow }) {
  return (
    <section className="school-section">
      <h3 className="school-section-title">{title}</h3>
      {items.length > 0 ? (
        <ul className="school-event-list">{items.map(renderRow)}</ul>
      ) : (
        <p className="school-section-empty">{emptyLabel}</p>
      )}
    </section>
  );
}

function CollapsibleInfoSection({ summary, infoItems, renderInfoRow }) {
  const [open, setOpen] = useState(false);
  if (!summary && infoItems.length === 0) return null;

  const closedLabel =
    infoItems.length > 0
      ? `Infos (${infoItems.length})`
      : "Kurz zusammengefasst";

  return (
    <div className="school-collapsible">
      <button
        type="button"
        className="school-collapsible-toggle"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? "Infos ausblenden" : closedLabel}
      </button>
      {open ? (
        <div className="school-collapsible-body">
          {summary ? (
            <div className="school-card-summary">
              <p className="school-card-summary-label">Kurz zusammengefasst</p>
              <p className="school-card-summary-text">{summary}</p>
            </div>
          ) : null}
          {infoItems.length > 0 ? (
            <ul className="school-event-list">{infoItems.map(renderInfoRow)}</ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function UpcomingAttentionList({ upcomingItems, extractions, onCalendarTap, onRefresh, kitaWeekdays = EMPTY_KITA_DAYS }) {
  const byId = useMemo(
    () => new Map(extractions.map((row) => [row.id, row])),
    [extractions]
  );

  if (upcomingItems.length === 0) {
    return (
      <p className="school-section-empty">
        Keine Fristen oder Termine in den nächsten {KITA_UPCOMING_DAYS} Tagen.
      </p>
    );
  }

  return (
    <section className="school-section" aria-label="Demnächst">
      <ul className="school-event-list">
        {upcomingItems.map((item) => {
          const row = byId.get(item.extractionId);
          const corrections = row?.corrections;
          const rawEvent = row?.events?.[item.eventIndex];
          return (
            <EventRow
              key={`${item.extractionId}-${item.eventIndex}`}
              event={item}
              rawEvent={rawEvent || item}
              corrections={corrections}
              adminMode={false}
              extractionId={item.extractionId}
              eventIndex={item.eventIndex}
              analyticsSource="upcoming"
              onCalendarTap={(provider) =>
                onCalendarTap?.(item.extractionId, item.eventIndex, provider)
              }
              onCorrectionsChange={onRefresh}
              kitaWeekdays={kitaWeekdays}
            />
          );
        })}
      </ul>
    </section>
  );
}

function ExtractionCard({
  item,
  adminMode,
  onSaved,
  onCalendarTap,
  onIrrelevant,
  onForgotten,
  householdLinked = false,
  kitaWeekdays = EMPTY_KITA_DAYS,
}) {
  const events = useMemo(() => item.events || [], [item.events]);
  const corrections = useMemo(
    () => item.corrections ?? EMPTY_CORRECTIONS,
    [item.corrections]
  );
  const shareEvents = useMemo(
    () => getShareableKiTaEvents({ events, corrections }),
    [events, corrections]
  );
  const summaryText = String(item.summary || "").trim();
  const canShare = !adminMode && canShareKiTaExtract({ summary: summaryText, events: shareEvents });
  const grouped = useMemo(() => groupSchoolEmailEvents(events), [events]);
  const partition = useMemo(
    () => partitionMailEvents(events, corrections, todayYmdZurich()),
    [events, corrections]
  );

  const [reviewStatus, setReviewStatus] = useState(item.reviewStatus || "unreviewed");
  const [missedCount, setMissedCount] = useState(item.missedCount ?? 0);
  const [wrongByIndex, setWrongByIndex] = useState(
    () => (corrections && corrections.wrongByIndex) || {}
  );
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [error, setError] = useState(null);
  const [sharingExtract, setSharingExtract] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggleWrong = useCallback((idx, checked) => {
    setWrongByIndex((prev) => {
      const next = { ...prev };
      if (checked) next[idx] = true;
      else delete next[idx];
      return next;
    });
  }, []);

  const handleShareExtract = async () => {
    setShareError(null);
    setSharingExtract(true);
    try {
      await shareKiTaExtractViaWhatsApp({
        summary: summaryText,
        events: shareEvents,
        extractionId: item.id,
      });
    } catch (err) {
      setShareError(err instanceof Error ? err.message : String(err));
    } finally {
      setSharingExtract(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSavedMsg(null);
    setSaving(true);
    try {
      const status = reviewStatus === "unreviewed" ? "ok" : reviewStatus;
      await submitSchoolEmailReview({
        id: item.id,
        reviewStatus: status,
        corrections: { wrongByIndex },
        missedCount: Number(missedCount) || 0,
      });
      setReviewStatus(status);
      setSavedMsg("Gespeichert.");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const parsedAttachments = (item.attachmentsMeta || []).filter(
    (a) => a.kind === "pdf"
  );

  const visible = (list) =>
    adminMode ? list : list.filter((event) => !isSchoolEventDismissed(corrections, event.index));

  const renderEventRow = (event) => (
    <EventRow
      key={event.index}
      event={event}
      rawEvent={events[event.index]}
      corrections={corrections}
      adminMode={adminMode}
      wrong={!!wrongByIndex[event.index]}
      onToggleWrong={(checked) => toggleWrong(event.index, checked)}
      extractionId={item.id}
      eventIndex={event.index}
      onCalendarTap={(provider) => onCalendarTap?.(item.id, event.index, provider)}
      onCorrectionsChange={onSaved}
      onIrrelevant={onIrrelevant}
      kitaWeekdays={kitaWeekdays}
    />
  );

  const handleForget = async () => {
    setShareError(null);
    setDeleting(true);
    try {
      const result = await forgetExtraction({ kind: "mail", id: item.id });
      setConfirmDelete(false);
      onForgotten?.({
        snapshotId: result.snapshotId,
        subject: item.subject,
        id: item.id,
      });
    } catch (err) {
      setShareError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <article className="school-card">
      <header className="school-card-head">
        <div>
          <p className="school-card-subject">{item.subject || "(kein Betreff)"}</p>
          <p className="school-card-from">
            {item.fromAddress || "(unbekannt)"} · {formatWhen(item.receivedAt)}
          </p>
        </div>
        <div className="school-card-head-actions">
          {canShare ? (
            <button
              type="button"
              className="school-btn"
              disabled={sharingExtract}
              onClick={handleShareExtract}
            >
              {sharingExtract ? "Öffne WhatsApp…" : "An Partner senden"}
            </button>
          ) : null}
          {adminMode ? (
            <div className="school-card-flags">
              {!item.allowlisted ? (
                <span className="school-flag school-flag--warn">nicht allowlisted</span>
              ) : null}
              <span className={`school-flag school-flag--${reviewStatus}`}>
                {reviewStatus === "unreviewed"
                  ? "unbewertet"
                  : reviewStatus === "ok"
                    ? "ok"
                    : "Fehler"}
              </span>
            </div>
          ) : null}
        </div>
      </header>
      {shareError ? (
        <p className="school-error" role="alert">
          {shareError}
        </p>
      ) : null}

      {adminMode && parsedAttachments.length > 0 ? (
        <p className="school-card-attachments">
          Anhänge:{" "}
          {parsedAttachments
            .map((a) => {
              const flags = [];
              if (a.excluded) flags.push("ausgeschlossen");
              else if (a.pending) flags.push("wartet");
              else if (!a.parsed) flags.push("kein Text");
              if (a.textSource) flags.push(a.textSource);
              if (a.source === "manual_upload") flags.push("manuell");
              if (a.source === "linked_document") flags.push("Link");
              const suffix = flags.length ? ` (${flags.join(", ")})` : "";
              return `${a.filename}${suffix}`;
            })
            .join(", ")}
        </p>
      ) : null}

      {!adminMode && isPdfStillProcessing(item) ? (
        <PdfProcessingBlock
          showUploadHint={needsPdfUploadWhilePending(item)}
          linked={linkedDocumentOnly(item)}
        />
      ) : null}

      {!adminMode && (needsPdfUpload(item) || needsPdfUploadWhilePending(item)) ? (
        <PdfUploadBlock item={item} onUploaded={onSaved} />
      ) : null}

      {adminMode ? (
        <>
          {item.summary ? (
            <div className="school-card-summary">
              <p className="school-card-summary-label">Kurz zusammengefasst</p>
              <p className="school-card-summary-text">{item.summary}</p>
            </div>
          ) : null}

          <ExtractionSection
            title="Aufgaben"
            items={visible(grouped.tasks)}
            emptyLabel="Keine Aufgaben erkannt."
            renderRow={renderEventRow}
          />

          <ExtractionSection
            title="Termine"
            items={visible(grouped.events)}
            emptyLabel="Keine Termine erkannt."
            renderRow={renderEventRow}
          />

          <ExtractionSection
            title="Infos"
            items={grouped.info}
            emptyLabel="Keine zusätzlichen Infos."
            renderRow={(event) => (
              <InfoRow
                key={event.index}
                event={event}
                adminMode={adminMode}
                wrong={!!wrongByIndex[event.index]}
                onToggleWrong={(checked) => toggleWrong(event.index, checked)}
              />
            )}
          />
        </>
      ) : (
        <>
          <ExtractionSection
            title="Fristen & Termine"
            items={visible(partition.upcomingAttention)}
            emptyLabel="Keine anstehenden Fristen oder Termine in dieser Mail."
            renderRow={renderEventRow}
          />

          {item.summary || partition.info.length > 0 ? (
            <CollapsibleInfoSection
              summary={item.summary}
              infoItems={partition.info}
              renderInfoRow={(event) => (
                <InfoRow
                  key={event.index}
                  event={event}
                  adminMode={adminMode}
                  wrong={!!wrongByIndex[event.index]}
                  onToggleWrong={(checked) => toggleWrong(event.index, checked)}
                />
              )}
            />
          ) : null}
        </>
      )}

      {!adminMode ? (
        <div className="school-card-forget">
          {confirmDelete ? (
            <div className="school-card-forget-confirm">
              <p>
                Mail und alle Termine daraus löschen?
                {householdLinked ? " Das gilt auch für deinen Partner." : ""}
              </p>
              <div className="school-edit-actions">
                <button
                  type="button"
                  className="school-btn school-btn--danger"
                  disabled={deleting}
                  onClick={handleForget}
                >
                  {deleting ? "Löscht…" : "Löschen"}
                </button>
                <button
                  type="button"
                  className="school-btn"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(false)}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="school-card-forget-btn"
              onClick={() => setConfirmDelete(true)}
            >
              Löschen
            </button>
          )}
        </div>
      ) : null}

      {adminMode ? (
        <footer className="school-card-review">
          <label className="school-review-field">
            <span>Verpasste Termine (nicht erkannt)</span>
            <input
              type="number"
              min={0}
              value={missedCount}
              onChange={(e) => setMissedCount(e.target.value)}
            />
          </label>
          <label className="school-review-field">
            <span>Status</span>
            <select
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
            >
              <option value="unreviewed">unbewertet</option>
              <option value="ok">ok</option>
              <option value="had_errors">hatte Fehler</option>
            </select>
          </label>
          <button
            type="button"
            className="school-btn school-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Speichert…" : "Bewertung speichern"}
          </button>
          {savedMsg ? <span className="school-saved">{savedMsg}</span> : null}
          {error ? <span className="school-error">{error}</span> : null}
        </footer>
      ) : null}
    </article>
  );
}

function AdminExtractionStats({ days = 30 }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSchoolExtractionStats({ days })
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (loading) {
    return <p className="school-muted">Statistik lädt…</p>;
  }
  if (error) {
    return <p className="school-error">{error}</p>;
  }
  if (!stats) return null;

  return (
    <section className="school-admin-stats" aria-label="Erkennungsqualität">
      <h3 className="school-admin-stats-title">Erkennungsqualität ({stats.days} Tage)</h3>
      <div className="school-admin-stats-grid">
        <div className="school-admin-stat">
          <span className="school-admin-stat-value">{stats.extractionCount}</span>
          <span className="school-admin-stat-label">E-Mails</span>
        </div>
        <div className="school-admin-stat">
          <span className="school-admin-stat-value">{stats.totalEvents}</span>
          <span className="school-admin-stat-label">Termine extrahiert</span>
        </div>
        <div className="school-admin-stat">
          <span className="school-admin-stat-value">{stats.totalFeedbackRows}</span>
          <span className="school-admin-stat-label">Korrekturen</span>
        </div>
        <div className="school-admin-stat">
          <span className="school-admin-stat-value">{stats.totalMissed}</span>
          <span className="school-admin-stat-label">Verpasst (Review)</span>
        </div>
      </div>
      {stats.issueBreakdown?.length > 0 ? (
        <ul className="school-admin-stats-issues">
          {stats.issueBreakdown.map((row) => (
            <li key={row.issueType}>
              <span>{row.label}</span>
              <span>
                {row.count} ({row.pct}%)
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="school-muted">Noch keine Nutzer-Korrekturen im Zeitraum.</p>
      )}
    </section>
  );
}

/**
 * @param {{ adminMode?: boolean, loginRedirect?: string }} props
 */
function KitaShareBanner({ onDismiss, hasHousehold = false }) {
  const [sharing, setSharing] = useState(null);
  const handleShare = async (kind) => {
    setSharing(kind);
    try {
      if (kind === "partner") {
        await shareKiTaPartnerViaWhatsApp();
      } else {
        await shareKiTaPeerViaWhatsApp();
      }
    } finally {
      setSharing(null);
    }
  };
  return (
    <aside className="school-share-banner" role="region" aria-label="KiTa teilen">
      <p className="school-share-banner-text">
        {buildKitaShareChatMessage({ hasHousehold })}
      </p>
      <div className="school-share-banner-actions">
        {hasHousehold ? (
          <button
            type="button"
            className="school-btn school-btn--primary"
            disabled={sharing !== null}
            onClick={() => handleShare("peer")}
          >
            {sharing === "peer" ? "Öffne WhatsApp…" : "Eltern weiterempfehlen"}
          </button>
        ) : (
          <button
            type="button"
            className="school-btn school-btn--primary"
            disabled={sharing !== null}
            onClick={() => handleShare("partner")}
          >
            {sharing === "partner" ? "Öffne WhatsApp…" : "Partner einladen"}
          </button>
        )}
        <button type="button" className="school-btn" onClick={onDismiss}>
          Später
        </button>
      </div>
    </aside>
  );
}

/**
 * @param {{ adminMode?: boolean, loginRedirect?: string }} props
 */
export function SchoolInboxView({ adminMode = false, loginRedirect = "/schule" }) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [kitaWeekdays, setKitaWeekdays] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [showShareBanner, setShowShareBanner] = useState(false);
  const [householdLinked, setHouseholdLinked] = useState(false);
  const [undo, setUndo] = useState(null);
  const undoTimerRef = useRef(null);
  const inboxViewTrackedRef = useRef(false);
  const prevExtractionCountRef = useRef(null);
  const shareBannerTrackedRef = useRef(false);

  const showShareBannerWithTracking = useCallback((trigger) => {
    setShowShareBanner(true);
    if (shareBannerTrackedRef.current) return;
    shareBannerTrackedRef.current = true;
    trackSharePromptShown({
      surface: "schule",
      trigger: trigger || getSharePromptContext().trigger || "wedge_win",
    });
  }, []);

  const handleCalendarTap = useCallback(
    (extractionId, eventIndex, provider) => {
      trackSchoolCalendarTap({
        extraction_id: extractionId,
        event_index: eventIndex,
        source: "inbox",
        provider: provider === "outlook" ? "outlook" : "google",
      });
      if (!isSharePromptShown()) {
        const eventCount = items.reduce(
          (sum, row) => sum + getShareableKiTaEvents(row).length,
          0,
        );
        markSharePromptShown({ trigger: "calendar_tap", eventCount });
        showShareBannerWithTracking("calendar_tap");
      } else if (shouldShowShareBanner()) {
        showShareBannerWithTracking(getSharePromptContext().trigger);
      }
    },
    [items, showShareBannerWithTracking],
  );

  useEffect(() => {
    if (adminMode || listLoading) return;
    if (shouldShowShareBanner()) {
      showShareBannerWithTracking(getSharePromptContext().trigger);
    }
  }, [adminMode, listLoading, showShareBannerWithTracking]);

  const load = useCallback(async () => {
    setListError(null);
    setListLoading(true);
    try {
      const [data, profile, household] = await Promise.all([
        fetchSchoolEmails(),
        fetchFamilyProfile().catch(() => null),
        fetchHousehold().catch(() => ({ linked: false })),
      ]);
      const nextItems = Array.isArray(data.items) ? data.items : [];
      setItems(nextItems);
      setKitaWeekdays(Array.isArray(profile?.children) ? profile.children : []);
      setHouseholdLinked(Boolean(household?.linked));

      const extractionCount = nextItems.length;
      const eventCount = nextItems.reduce(
        (sum, row) => sum + (Array.isArray(row.events) ? row.events.length : 0),
        0,
      );

      if (!inboxViewTrackedRef.current) {
        inboxViewTrackedRef.current = true;
        trackEvent(ANALYTICS.school_inbox_viewed, {
          extraction_count: extractionCount,
          event_count: eventCount,
        });
      }

      if (
        prevExtractionCountRef.current === 0 &&
        extractionCount > 0 &&
        !isFirstExtractionCelebrated()
      ) {
        markFirstExtractionCelebrated();
        try {
          localStorage.setItem("albis.pendingFirstExtractionChat", "1");
        } catch {
          /* ignore */
        }
        trackEvent(ANALYTICS.school_first_extraction_seen, {
          extraction_count: extractionCount,
          event_count: eventCount,
        });
      }
      prevExtractionCountRef.current = extractionCount;
    } catch (err) {
      setItems([]);
      setListError(err instanceof Error ? err.message : String(err));
    } finally {
      setListLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await load();
  }, [load]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(loginRedirect)}`, {
        replace: true,
      });
      return;
    }
    if (adminMode && !userMaySeeActivitiesImportNav(user)) {
      navigate("/mehr", { replace: true });
    }
  }, [user, loading, navigate, adminMode, loginRedirect]);

  useEffect(() => {
    if (user && (!adminMode || userMaySeeActivitiesImportNav(user))) {
      refreshAll();
    }
  }, [user, adminMode, refreshAll]);

  useEffect(() => {
    const hasProcessing = items.some(isPdfStillProcessing);
    if (!hasProcessing || !user) return undefined;
    const timer = window.setInterval(() => {
      refreshAll();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items, user, refreshAll]);

  const clearUndoTimer = () => {
    if (undoTimerRef.current) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  };

  const armUndo = (next) => {
    clearUndoTimer();
    setUndo(next);
    undoTimerRef.current = window.setTimeout(() => {
      if (next?.snapshotId) {
        purgeExtractionSnapshot(next.snapshotId).catch(() => {});
      }
      setUndo(null);
    }, 8000);
  };

  const handleIrrelevant = (info) => {
    armUndo({
      hint: "Nicht relevant",
      title: info.title,
      extractionId: info.extractionId,
      eventIndex: info.eventIndex,
    });
  };

  const handleForgotten = (info) => {
    setItems((prev) => prev.filter((row) => row.id !== info.id));
    armUndo({
      hint: "Mail gelöscht",
      title: info.subject || "",
      snapshotId: info.snapshotId,
    });
  };

  const handleUndo = async () => {
    const current = undo;
    clearUndoTimer();
    setUndo(null);
    try {
      if (current?.snapshotId) {
        await restoreExtraction(current.snapshotId);
      } else if (current?.extractionId != null && Number.isInteger(current.eventIndex)) {
        await patchSchoolEmailEventOverride(current.extractionId, current.eventIndex, {
          dismiss: false,
        });
      }
      await refreshAll();
    } catch (err) {
      setListError(err instanceof Error ? err.message : String(err));
    }
  };

  if (loading) {
    return (
      <div className="school-inbox-page">
        <p className="school-muted">Laden…</p>
      </div>
    );
  }

  if (!user || (adminMode && !userMaySeeActivitiesImportNav(user))) {
    return null;
  }

  return (
    <div className="school-inbox-page">
      <header className="school-inbox-header">
        <h2 className="school-inbox-title">
          {adminMode ? "Schul-E-Mails (Admin)" : "KiTa & Kindergarten"}
        </h2>
        <p className="school-inbox-lead">
          {adminMode
            ? "Weitergeleitete E-Mails und Erkennungsqualität prüfen."
            : "Einzelne Mails prüfen — Fristen und Erinnerungen findest du im Chat."}
        </p>
        <div className="school-inbox-actions">
          <Link to="/mehr" className="school-inbox-back">
            ← Mehr
          </Link>
          {!adminMode ? (
            <>
              <Link to="/" className="school-inbox-back">
                Zu Albis
              </Link>
              <Link to="/mehr" className="school-inbox-back">
                Weiterleitung einrichten
              </Link>
            </>
          ) : null}
          <button
            type="button"
            className="school-btn"
            onClick={refreshAll}
            disabled={listLoading}
          >
            {listLoading ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>
      </header>

      {listError ? <p className="school-error">{listError}</p> : null}

      {undo ? (
        <div className="school-undo" role="status">
          <p>
            {undo.hint}
            {undo.title ? `: ${undo.title}` : ""}
          </p>
          <button type="button" className="school-btn" onClick={handleUndo}>
            Rückgängig
          </button>
        </div>
      ) : null}

      {adminMode ? <AdminExtractionStats days={30} /> : null}

      {!adminMode && showShareBanner ? (
        <KitaShareBanner
          hasHousehold={householdLinked}
          onDismiss={() => {
            markShareBannerDismissed();
            trackSharePromptDismissed({ surface: "schule" });
            setShowShareBanner(false);
          }}
        />
      ) : null}

      <div id="school-panel-mail">
        {!listLoading && items.length === 0 ? (
          <p className="school-card-empty">
            Noch nichts gemerkt. Im Chat die Adresse kopieren und die letzte Eltern-Mail
            weiterleiten — oder ein Foto vom Flyer.
          </p>
        ) : null}

        <div className="school-inbox-list">
          {items.map((item) => (
            <ExtractionCard
              key={item.id}
              item={item}
              adminMode={adminMode}
              onSaved={refreshAll}
              onCalendarTap={handleCalendarTap}
              onIrrelevant={handleIrrelevant}
              onForgotten={handleForgotten}
              householdLinked={householdLinked}
              kitaWeekdays={kitaWeekdays}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Admin accuracy-review inbox (existing /admin/school-inbox route). */
export default function SchoolInboxPage() {
  return <SchoolInboxView adminMode loginRedirect="/admin/school-inbox" />;
}
